import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { type OrderListQuery, type OrderMoneyInput, ordersApi } from '@/features/orders/api';
import { ApiError } from '@/lib/api';
import { signedLinkRefresh } from '@/lib/signedLinks';

/*
  One query per page of the register, keyed by the whole filter set, sort and page. Like the
  product register, this one is paged on the server (4,135 orders), so a change of filter, sort or
  page is a new query; the previous page stays on screen while the next one loads so the table
  never blinks to a skeleton between pages.
*/
export const orderKeys = {
    all: ['orders'] as const,
    list: (query: OrderListQuery) => ['orders', 'list', query] as const,
    overview: (id: number) => ['orders', 'overview', id] as const,
    record: (id: number) => ['orders', 'record', id] as const,
    options: (id: number) => ['orders', 'options', id] as const,
    lookups: ['orders', 'lookups'] as const,
    products: ['orders', 'products'] as const,
    newOptions: ['orders', 'new', 'options'] as const,
    newClient: (customerId: number) => ['orders', 'new', 'client', customerId] as const,
    nextNumber: (year: number) => ['orders', 'new', 'number', year] as const,
    lineLists: ['orders', 'line-lists'] as const,
    money: (input: OrderMoneyInput) => ['orders', 'money', input] as const,
    comments: (id: number) => ['orders', 'comments', id] as const,
    lineProduct: (productId: number, orderDate: string) =>
        ['orders', 'line-product', productId, orderDate] as const,
    newLookbooks: (customerId: number) => ['orders', 'new', 'lookbooks', customerId] as const,
    newLookbookProducts: (lookbookId: number, customerId: number) =>
        ['orders', 'new', 'lookbook', lookbookId, customerId] as const,
};

/**
 * An order's money for what the form holds now. The last answer stays on screen while the next
 * one is worked out, so the totals never blink to nothing between keystrokes.
 */
export const orderMoneyQuery = (input: OrderMoneyInput) =>
    queryOptions({
        queryKey: orderKeys.money(input),
        queryFn: () => ordersApi.money(input),
        placeholderData: keepPreviousData,
    });

export const ordersQuery = (query: OrderListQuery) =>
    queryOptions({
        queryKey: orderKeys.list(query),
        queryFn: () => ordersApi.list(query),
        placeholderData: keepPreviousData,
    });

/**
 * The drawer's lists. Loaded with the page rather than with the drawer, because the bar has to
 * label an applied filter's chip ("Brand: Gucci", not "Brand: 1243") before anybody opens it.
 * Fifteen lists and about 175 KB, fresh for the session.
 */
export const orderLookupsQuery = queryOptions({
    queryKey: orderKeys.lookups,
    queryFn: ordersApi.lookups,
    staleTime: Infinity,
});

/**
 * The products a filter could match: 13,297 rows, 680 KB raw and 186 KB gzipped, fresh for the
 * session.
 *
 * Loaded with the page rather than with the drawer. Deferring it saves that 186 KB on a visit
 * that never filters, and costs a re-render of the drawer at the moment the list lands, which
 * detaches whatever dropdown the reader had open. The saving is not worth the race.
 */
export const orderProductsQuery = queryOptions({
    queryKey: orderKeys.products,
    queryFn: ordersApi.products,
    staleTime: Infinity,
});

/**
 * An opened row's lines and facts, asked for when the row opens rather than carried on every
 * row: one production order has 170 lines. Fresh for a minute, so closing a row and opening it
 * again, or opening the one beside it and coming back, costs nothing. Its waybills link to their
 * labels, which are signed for a few minutes (ALS-493), so a row left open is asked again before
 * they run out.
 */
export const orderOverviewQuery = (id: number) =>
    queryOptions({
        queryKey: orderKeys.overview(id),
        queryFn: () => ordersApi.overview(id),
        ...signedLinkRefresh,
        staleTime: 60_000,
    });

/**
 * One order, whole, for its detail page. A 404 is an answer rather than a hiccup, so it is not
 * asked again: the page says the order is not there.
 */
export const orderRecordQuery = (id: number) =>
    queryOptions({
        queryKey: orderKeys.record(id),
        queryFn: () => ordersApi.record(id),
        retry: (failures, error) =>
            !(error instanceof ApiError && error.status === 404) && failures < 1,
    });

/**
 * Edit order's lists: the sale sources, the team, and the client's own special dates and saved
 * addresses. Asked for when the drawer opens, and fresh for as long as the page is.
 */
export const orderOptionsQuery = (id: number) =>
    queryOptions({
        queryKey: orderKeys.options(id),
        queryFn: () => ordersApi.options(id),
        staleTime: 5 * 60_000,
    });

/**
 * New order's lists (ALS-62): the client search, the sale sources, the sales team and the
 * currencies. Fresh for a minute, so a client added a moment ago is found the next time the page
 * opens; adding one inline refreshes it there and then.
 */
export const newOrderOptionsQuery = queryOptions({
    queryKey: orderKeys.newOptions,
    queryFn: ordersApi.newOptions,
    staleTime: 60_000,
});

/**
 * What choosing a client fills in. Read again each time the client is chosen rather than kept,
 * so the grid is built from the record as it is now (ALS-62).
 */
export const newOrderClientQuery = (customerId: number) =>
    queryOptions({
        queryKey: orderKeys.newClient(customerId),
        queryFn: () => ordersApi.newClient(customerId),
        staleTime: 0,
        // A client deactivated meanwhile answers 404, which is an answer rather than a hiccup.
        retry: (failures, error) =>
            !(error instanceof ApiError && error.status === 404) && failures < 1,
    });

/**
 * The number the order would get. Keyed by the order date's year, because that is what the
 * counter is kept by; a preview only, so it is never trusted for long.
 */
export const nextOrderNumberQuery = (orderDate: string) =>
    queryOptions({
        queryKey: orderKeys.nextNumber(Number(orderDate.slice(0, 4))),
        queryFn: () => ordersApi.nextNumber(orderDate),
        staleTime: 30_000,
    });

/**
 * The tax register and the suppliers every line editor chooses from (ALS-63). The same for every
 * line, so read once and kept for the session.
 */
export const orderLineListsQuery = queryOptions({
    queryKey: orderKeys.lineLists,
    queryFn: ordersApi.lineLists,
    staleTime: Infinity,
});

/** The lookbooks a client sees (ALS-64), read when Add from lookbook opens. */
export const newOrderLookbooksQuery = (customerId: number) =>
    queryOptions({
        queryKey: orderKeys.newLookbooks(customerId),
        queryFn: () => ordersApi.newLookbooks(customerId),
        staleTime: 60_000,
    });

/** One lookbook's products, read when the book is chosen. */
export const newOrderLookbookProductsQuery = (lookbookId: number, customerId: number) =>
    queryOptions({
        queryKey: orderKeys.newLookbookProducts(lookbookId, customerId),
        queryFn: () => ordersApi.newLookbookProducts(lookbookId, customerId),
        staleTime: 60_000,
    });

/**
 * A product as a line starts from it, with commission as of the order date. Read when it is
 * ticked; the same product ticked again in the same drawer costs nothing.
 */
export const lineProductQuery = (productId: number, orderDate: string) =>
    queryOptions({
        queryKey: orderKeys.lineProduct(productId, orderDate),
        queryFn: async () => (await ordersApi.lineProducts([productId], orderDate)).at(0) ?? null,
        staleTime: 60_000,
    });

/** What New order opens with to raise a cancelled order again (ALS-260). Read fresh each time. */
export const orderReraiseQuery = (id: number) =>
    queryOptions({
        queryKey: ['orders', 'reraise', id] as const,
        queryFn: () => ordersApi.reraise(id),
        staleTime: 0,
        retry: false,
    });

/**
 * An order's internal comments (ALS-65), read with its page. Their files are linked to signed for a
 * few minutes (ALS-493), so the comments are kept fresher than that.
 */
export const orderCommentsQuery = (id: number) =>
    queryOptions({
        queryKey: orderKeys.comments(id),
        queryFn: () => ordersApi.comments(id),
        ...signedLinkRefresh,
    });
